import { createAssetReference, assetReferenceKey } from './asset_reference_contract.mjs';
import { createLegacyAssetAppearance } from './asset_binding_contract.mjs';

// Order-wide asset state of one system (U08c, user decision 05.10.2026): exactly one
// pinned default revision (plus its colour state) per article in the whole order.
// Variants are not stored; they follow from the pinned base and the drawing.
export const ASSET_PINS_CONTRACT = 'ks.asset-pins';
const check = (ok, message) => { if (!ok) throw Object.assign(new TypeError(`Asset pins: ${message}`), { code: 'INVALID_ASSET_PINS' }); };
function fields(value, keys, optional = []) {
  check(value && typeof value === 'object' && !Array.isArray(value), 'object required');
  const descriptors = Object.getOwnPropertyDescriptors(value), names = Reflect.ownKeys(descriptors);
  check(keys.every(key => names.includes(key)) && names.every(key => keys.includes(key) || optional.includes(key))
    && names.every(key => descriptors[key].enumerable && Object.hasOwn(descriptors[key], 'value')), 'unexpected or accessor fields');
  return value;
}
const articleKey = articleNumber => String(articleNumber).trim().toLowerCase();

/** Validated, detached and frozen pin table; one pin per article (case-insensitive). */
export function createAssetPins(value) {
  const table = fields(value, ['contract', 'contractVersion', 'pins']);
  check(table.contract === ASSET_PINS_CONTRACT && table.contractVersion === 1, 'unsupported contract');
  check(Array.isArray(table.pins) && table.pins.length <= 512, 'bounded pin list required');
  const seen = new Set();
  const pins = table.pins.map(raw => {
    const pin = fields(raw, ['articleNumber', 'reference'], ['appearance']);
    check(typeof pin.articleNumber === 'string' && pin.articleNumber.trim() === pin.articleNumber
      && pin.articleNumber.length > 0 && pin.articleNumber.length <= 128, 'article number');
    check(!seen.has(articleKey(pin.articleNumber)), 'duplicate article');
    seen.add(articleKey(pin.articleNumber));
    const reference = createAssetReference(pin.reference);
    check(reference.variantId === 'default', 'default revision required');
    return Object.freeze({ articleNumber: pin.articleNumber, reference,
      ...(Object.hasOwn(pin, 'appearance') ? { appearance: createLegacyAssetAppearance(pin.appearance) } : {}) });
  });
  return Object.freeze({ contract: ASSET_PINS_CONTRACT, contractVersion: 1, pins: Object.freeze(pins) });
}

export const emptyAssetPins = () => ({ contract: ASSET_PINS_CONTRACT, contractVersion: 1, pins: [] });

/** The order's pin for an article, or null. */
export function assetPinFor(table, articleNumber) {
  const key = articleKey(articleNumber);
  return table?.pins.find(pin => articleKey(pin.articleNumber) === key) || null;
}

/** True when a committed pin (or a variant of its pinned base revision) authorizes the locator. */
export function assetPinsAuthorize(table, reference, locator = null) {
  if (!table) return false;
  if (reference) {
    const key = assetReferenceKey(reference);
    if (table.pins.some(pin => assetReferenceKey(pin.reference) === key)) return true;
  }
  const target = locator || reference;
  return Boolean(target) && table.pins.some(pin => pin.reference.assetObjectId === target.assetObjectId
    && pin.reference.revisionId === target.revisionId && (reference ? reference.packageDigest === pin.reference.packageDigest : true));
}

/** Read the declared pin path relative to one owner's order data. Missing stays missing. */
export function readSystemAssetPins(contract, data) {
  if (!contract?.assetPinsPath) return null;
  let value = data;
  for (const segment of contract.assetPinsPath) {
    if (value === undefined) return null;
    check(value !== null && typeof value === 'object' && !Array.isArray(value), 'invalid path container');
    const descriptor = Object.getOwnPropertyDescriptor(value, segment);
    if (!descriptor) return null;
    check(Object.hasOwn(descriptor, 'value'), 'path accessor'); value = descriptor.value;
  }
  return createAssetPins(value);
}
